/**
 * NuGuard AI - Main Application Component
 *
 * Root component with React Router for navigation.
 * Handles authentication state and provides routing structure.
 * Uses React.lazy for route-level code splitting.
 */

import React, { Suspense, useCallback, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, type Location } from 'react-router-dom';
import Layout from './components/Layout';
import Login from './components/Login';
import OAuthCallback from './components/OAuthCallback';
import NotificationPortal from './components/NotificationPortal';
import ErrorBoundary from './components/ErrorBoundary';
import { LoadingSpinner } from './components/ui/LoadingSpinner';
import { GlobalTooltip } from './components/ui/GlobalTooltip';
import { User } from './types';
import { useAuth, useIdleTimeout } from './hooks';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { NotificationProvider, useNotify } from './contexts/NotificationContext';
import { useLicensingNotifications } from './hooks/useLicensingNotifications';
import { Loader2 } from 'lucide-react';
import { CONFIG } from './config';

// Route-level code splitting: each page loads on demand
const DashboardV2 = React.lazy(() => import('./components/dashboard-v2/DashboardV2'));
const Applications = React.lazy(() => import('./components/Applications'));
const PolicyCenter = React.lazy(() => import('./components/PolicyCenter'));
const Settings = React.lazy(() => import('./components/Settings'));
const Reports = React.lazy(() => import('./components/Reports'));
const RiskCatalog = React.lazy(() => import('./components/RiskCatalog'));
const RiskCatalogRecord = React.lazy(() => import('./components/RiskCatalogRecord'));
const ReportViewer = React.lazy(() => import('./components/ReportViewer'));
const ApplicationWorkspace = React.lazy(() => import('./components/ApplicationWorkspace'));
const Tenants = React.lazy(() => import('./components/Tenants'));
const QuickStart = React.lazy(() => import('./components/QuickStart'));

/**
 * Suspense fallback for lazy-loaded routes
 */
const RouteFallback: React.FC = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <LoadingSpinner size="lg" message="Loading..." />
  </div>
);

/**
 * Protected Route wrapper - redirects to login if not authenticated
 */
const ProtectedRoute: React.FC<{
  user: User | null;
  children: React.ReactNode;
}> = ({ user, children }) => {
  const location = useLocation();
  if (CONFIG.DEBUG_FRONTEND_LOGS) {
    console.debug('[route] ProtectedRoute evaluated', { hasUser: Boolean(user) });
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
};

const PlatformRoute: React.FC<{
  user: User | null;
  children: React.ReactNode;
}> = ({ user, children }) => {
  const location = useLocation();
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (user.scope !== 'platform') {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

/**
 * Login route – redirects already-authenticated users back to where they came from,
 * or to "/" if there is no prior location stored in router state.
 */
const LoginRoute: React.FC<{
  user: User | null;
  onLogin: (user: User) => void;
  defaultMode?: 'login' | 'signup';
}> = ({ user, onLogin, defaultMode }) => {
  const location = useLocation();
  if (user) {
    const from = (location.state as { from?: Location } | null)?.from;
    const dest = from ? `${from.pathname}${from.search}${from.hash}` : '/';
    return <Navigate to={dest} replace />;
  }
  const from = (location.state as { from?: Location } | null)?.from;
  const postLoginRedirect = from ? `${from.pathname}${from.search}${from.hash}` : undefined;
  // Remount on every navigation to this route (key={location.key}) so Login's internal
  // mode/resetDone/email state — which is only computed in useState initializers — is
  // recomputed from the fresh URL/state instead of persisting stale across e.g. the
  // reset-password "Back to sign in" navigate() call, which stays on the same /login path.
  return <Login key={location.key} onLogin={onLogin} postLoginRedirect={postLoginRedirect} initialMode={defaultMode} />;
};

const RouterDiagnostics: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    if (CONFIG.DEBUG_FRONTEND_LOGS) {
      console.info('[route] location changed', {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      });
    }
  }, [location]);

  return null;
};

const RoutedGlobalTooltip: React.FC = () => {
  const location = useLocation();
  return <GlobalTooltip key={`${location.pathname}${location.search}${location.hash}`} />;
};

/**
 * Main App Component with Router
 */
const AppContent: React.FC = () => {
  const { notifications, removeNotification } = useNotify();
  const auth = useAuth();
  const { theme, toggleTheme } = useTheme();

  useLicensingNotifications(auth.isAuthenticated, auth.user?.role === 'ADMIN');

  useEffect(() => {
    if (CONFIG.DEBUG_FRONTEND_LOGS) {
      console.info('[app] auth state changed', {
        loading: auth.loading,
        isAuthenticated: auth.isAuthenticated,
        userId: auth.user?.id ?? null,
        role: auth.user?.role ?? null,
      });
    }
  }, [auth.loading, auth.isAuthenticated, auth.user?.id, auth.user?.role]);

  // Handle login
  const handleLogin = useCallback(async (loggedInUser: User) => {
    await auth.login(loggedInUser);
  }, [auth]);

  // Handle logout
  const handleLogout = useCallback(async () => {
    await auth.logout();
  }, [auth]);

  // Force logout after a period of no user interaction, so an open-but-idle
  // tab doesn't stay authenticated indefinitely.
  const handleIdle = useCallback(() => {
    if (CONFIG.DEBUG_FRONTEND_LOGS) {
      console.info('[auth] idle timeout reached, logging out');
    }
    void handleLogout();
  }, [handleLogout]);
  useIdleTimeout(auth.isAuthenticated, handleIdle);

  // Loading state
  if (auth.loading) {
    return (
      <div className="min-h-screen bg-nuguard-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
          <span className="text-slate-400">Loading NuGuard AI...</span>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <RouterDiagnostics />
      <Routes>
        {/* Public Route: Login */}
        <Route
          path="/login"
          element={<LoginRoute user={auth.user} onLogin={handleLogin} />}
        />
        {/* /signup renders the Login component directly in signup mode — no redirect, no ?mode= param */}
        <Route
          path="/signup"
          element={<LoginRoute user={auth.user} onLogin={handleLogin} defaultMode="signup" />}
        />
        <Route
          path="/auth/callback"
          element={<OAuthCallback onLogin={handleLogin} />}
        />

        {/* Protected Routes with Layout */}
        <Route
          element={
            <ProtectedRoute user={auth.user}>
              <Layout user={auth.user!} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        >
          <Route index element={<ErrorBoundary><Suspense fallback={<RouteFallback />}><DashboardV2 /></Suspense></ErrorBoundary>} />
          <Route path="applications" element={<ErrorBoundary><Suspense fallback={<RouteFallback />}><Applications /></Suspense></ErrorBoundary>} />
          <Route path="applications/:id" element={<ErrorBoundary><Suspense fallback={<RouteFallback />}><ApplicationWorkspace /></Suspense></ErrorBoundary>} />
          <Route path="risks" element={<ErrorBoundary><Suspense fallback={<RouteFallback />}><ApplicationWorkspace /></Suspense></ErrorBoundary>} />
          <Route path="policies" element={<ErrorBoundary><Suspense fallback={<RouteFallback />}><PolicyCenter /></Suspense></ErrorBoundary>} />
          <Route path="reports" element={<ErrorBoundary><Suspense fallback={<RouteFallback />}><Reports /></Suspense></ErrorBoundary>} />
          {CONFIG.ENABLE_RISK_CATALOG && (
            <Route path="risk-catalog" element={<ErrorBoundary><Suspense fallback={<RouteFallback />}><RiskCatalog /></Suspense></ErrorBoundary>} />
          )}
          {CONFIG.ENABLE_RISK_CATALOG && (
            <Route path="risk-catalog/:recordId" element={<ErrorBoundary><Suspense fallback={<RouteFallback />}><RiskCatalogRecord /></Suspense></ErrorBoundary>} />
          )}
          <Route path="reports/:reportId/view" element={<ErrorBoundary><Suspense fallback={<RouteFallback />}><ReportViewer /></Suspense></ErrorBoundary>} />
          <Route
            path="tenants/*"
            element={
              <PlatformRoute user={auth.user}>
                <ErrorBoundary><Suspense fallback={<RouteFallback />}><Tenants /></Suspense></ErrorBoundary>
              </PlatformRoute>
            }
          />
          <Route path="quickstart" element={<ErrorBoundary><Suspense fallback={<RouteFallback />}><QuickStart /></Suspense></ErrorBoundary>} />
          <Route path="settings/*" element={<ErrorBoundary><Suspense fallback={<RouteFallback />}><Settings user={auth.user!} theme={theme} toggleTheme={toggleTheme} /></Suspense></ErrorBoundary>} />
        </Route>

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Global Notification Portal */}
      <NotificationPortal
        notifications={notifications}
        removeNotification={removeNotification}
      />

      <RoutedGlobalTooltip />
    </BrowserRouter>
  );
};

/**
 * Root App with Theme Provider and top-level error boundary
 */
const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <NotificationProvider>
          <AppContent />
        </NotificationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
